import loadImage from 'blueimp-load-image'
import convert from 'heic-convert/browser'

const MAX_UPLOAD_SIZE_BYTES = 5_000_000
const QUALITY_SEARCH_ITERATIONS = 10
const MIN_QUALITY = 0.5

export function modImageName(filename: string, extra: string): string {
  const fileParts = filename.split('.');
  const extension = fileParts.pop();
  const modifiedName = `${fileParts.join('_')}${extra}.${extension}`;

  return modifiedName;
}

async function convertHeicToJpegBlob(file: any) {
  const buffer = await file.arrayBuffer()
  const byteArray: any = new Int8Array(buffer)
  const convertedBuffer = await convert({
    buffer: byteArray,
    format: 'JPEG',
    quality: 1
  })
  const blob = new Blob([convertedBuffer], { type: 'image/jpeg' })
  return blob
}

async function processFile(file: any, maxDimension: number = 2000, mimeType: string = '', quality: number = 100) {
  const fileType = file.type?.toLowerCase() ?? ''
  const filenameLowerCase = file.name?.toLowerCase() ?? ''
  const isHeic = fileType.includes('heic') || filenameLowerCase.endsWith('.heic')
  const isImage = fileType.startsWith('image/') || /\.(avif|bmp|gif|jpe?g|png|svg|tiff?|webp)$/i.test(filenameLowerCase)
  let imageToProcess: File | Blob = file
  let filename = file.name

  if (isHeic) {
    imageToProcess = await convertHeicToJpegBlob(file)
    filename = modImageName(file.name, '_converted')
  }

  if (!isImage && !isHeic) {
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      throw new Error('Non-image files larger than 1 MB cannot be compressed.')
    }

    const fileBlob = new Blob([file], { type: file.type })
    return {
      imageToUpload: fileBlob,
      filename: file.name
    }
  }

  const canvas = await loadImageCanvas(imageToProcess, maxDimension)
  const imageToUpload = await compressCanvasToSize(canvas, MAX_UPLOAD_SIZE_BYTES, mimeType, quality)
  return { imageToUpload, filename }
}

export async function processImage(imageInput: any, maxDimension: number = 2000, mimeType: string = '', quality: number = 100) {
  let file: File = imageInput.files[0]
  return processFile(file, maxDimension, mimeType, quality)
}

export async function processMultipleImages(imageInput: any, maxDimension: number = 2000, mimeType: string = '', quality: number = 100) {
  const processedImages = []
  for (const file of imageInput.files) {
    // handle normal files
    const processedImage = await processFile(file, maxDimension, mimeType, quality)
    processedImages.push(processedImage)
  }
  return processedImages
}

function loadImageCanvas(file: File | Blob | string, maxDimension: number): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    loadImage(file, (img) => {
      if (!(img instanceof HTMLCanvasElement) || img.width <= 0 || img.height <= 0) {
        reject(new Error('Unable to decode image.'))
        return
      }

      resolve(img)
    }, {
      canvas: true,
      maxWidth: maxDimension,
      maxHeight: maxDimension,
      meta: true,
      orientation: true
    })
  })
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string = '', quality: number = 100): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob)
      } else {
        reject(new Error('Unable to encode image.'))
      }
    }, mimeType, quality/100)
  })
}

async function compressCanvasToSize(canvas: HTMLCanvasElement, maxSize: number, mimeType: string = '', quality: number = 100): Promise<Blob> {
  const sourceCanvas = canvas
  let currentCanvas = canvas

  while (true) {
    const lowestQualityBlob = await canvasToBlob(currentCanvas, mimeType, MIN_QUALITY)
    if (lowestQualityBlob.size <= maxSize) {
      const highestQualityBlob = await canvasToBlob(currentCanvas, mimeType, quality)
      if (highestQualityBlob.size <= maxSize) {
        return highestQualityBlob
      }

      let lowestQuality = 0
      let highestQuality = 1
      let bestFitBlob = lowestQualityBlob

      for (let i = 0; i < QUALITY_SEARCH_ITERATIONS; i++) {
        const quality = (lowestQuality + highestQuality) / 2
        const candidateBlob = await canvasToBlob(currentCanvas, mimeType, quality)
        if (candidateBlob.size <= maxSize) {
          lowestQuality = quality
          bestFitBlob = candidateBlob
        } else {
          highestQuality = quality
        }
      }

      return bestFitBlob
    }

    if (currentCanvas.width === 1 && currentCanvas.height === 1) {
      throw new Error('Error compressing image')
    }

    const scale = Math.min(0.9, Math.sqrt(maxSize / lowestQualityBlob.size) * 0.9)
    let width = Math.max(1, Math.floor(currentCanvas.width * scale))
    let height = Math.max(1, Math.floor(currentCanvas.height * scale))

    if (width === currentCanvas.width && height === currentCanvas.height) {
      if (currentCanvas.width >= currentCanvas.height && currentCanvas.width > 1) {
        width--
      } else {
        height--
      }
    }

    const smallerCanvas = document.createElement('canvas')
    smallerCanvas.width = width
    smallerCanvas.height = height

    const context = smallerCanvas.getContext('2d')
    if (!context) {
      throw new Error('Unable to resize image.')
    }
    context.drawImage(sourceCanvas, 0, 0, width, height)
    currentCanvas = smallerCanvas
  }
}
